import { getAnimationData } from "lib/animatedElementUtils";
import { updateDurationForDesign } from "lib/designUtils";
import Logger from "lib/logger";
import { EditorElementsOps as EditorElementOps } from "views/components/Editor/editorOps";
import { cloneDeep } from "lib";
import {
  ANIMATION_DURATION,
  calculateTotalAnimationDuration
} from "lib/animationUtils";

/**
 * A file containing the functions that are used across multiple, if not all, action bar containers.
 */
const ActionBarOps = {
  onElementsAttributesChange(elementUpdates, containerProps, callback) {
    Logger.info("ActionBarOps.onElementsAttributesChange called");
    const updatedDesignData = containerProps.designData.updateElementsAttributes(
      {
        elementUpdates
      }
    );

    const isCssAnimationUpdate = Object.values(elementUpdates).some(update =>
      update.hasOwnProperty("cssAnimation")
    );

    if (isCssAnimationUpdate) {
      this.handleCssAnimationUpdate(
        elementUpdates,
        updatedDesignData,
        containerProps
      );
      return;
    }

    containerProps.updateContextState(updatedDesignData);
    containerProps.onSave(updatedDesignData, {});
    // TODO: What to do with the callback?
  },

  /**
   * Handle CSS animation updates for elements
   * @param {object} elementUpdates - The updates to apply to elements
   * @param {object} updatedDesignData - The design data with initial updates applied
   * @param {object} containerProps - Props of the calling container
   */
  handleCssAnimationUpdate(elementUpdates, updatedDesignData, containerProps) {
    const { designData } = containerProps;
    const pagesUpdated = cloneDeep(updatedDesignData.pages);
    const animatedUpdates = Object.values(elementUpdates).filter(update =>
      update.hasOwnProperty("cssAnimation")
    );

    animatedUpdates.forEach(element => {
      const elementData = updatedDesignData.elements[element.uniqueId];
      const pageAnimatedElements =
        pagesUpdated[element.pageId].animatedElements;

      const animationData = getAnimationData({
        ...elementData,
        pageId: element.pageId
      });

      const elementAlreadyHasAnimation = animationData.some(
        dataPoint => dataPoint.animationDataKey in pageAnimatedElements
      );

      if (elementAlreadyHasAnimation) {
        if (
          !element.cssAnimation ||
          (Array.isArray(element.cssAnimation) &&
            element.cssAnimation.length === 0)
        ) {
          // Element has no animations left, remove it from animatedElements
          animationData.forEach(dataPoint => {
            delete pagesUpdated[element.pageId].animatedElements[
              dataPoint.animationDataKey
            ];
          });
        } else {
          // need to handle the update of animation data
          // For textbox elements with letter/word animations, calculate the proper duration
          let cssAnimationDuration;
          if (
            elementData.type === "textbox" &&
            element.cssAnimation.textType &&
            (element.cssAnimation.textType === "letter" ||
              element.cssAnimation.textType === "word")
          ) {
            // Use calculateTotalAnimationDuration for letter/word animations
            cssAnimationDuration = calculateTotalAnimationDuration([
              { ...elementData, cssAnimation: element.cssAnimation }
            ]);
          } else {
            // Use default duration for other animation types
            cssAnimationDuration =
              element.cssAnimation.delay + ANIMATION_DURATION;
          }

          Object.values(pageAnimatedElements).forEach(dataPoint => {
            if (
              dataPoint.uniqueId === element.uniqueId &&
              cssAnimationDuration > dataPoint.duration
            ) {
              pagesUpdated[element.pageId].animatedElements[
                dataPoint.animationDataKey
              ].duration = cssAnimationDuration;
            }
          });
        }
      } else {
        // need to handle the addition of new animation data
        // Only add to animatedElements if the element actually has animations
        if (
          element.cssAnimation &&
          (!Array.isArray(element.cssAnimation) ||
            element.cssAnimation.length > 0)
        ) {
          animationData.forEach(dataPoint => {
            pagesUpdated[element.pageId].animatedElements[
              dataPoint.animationDataKey
            ] = dataPoint;
          });
        }
      }
    });

    updatedDesignData.pages = pagesUpdated;

    const designWithUpdatedDuration = updateDurationForDesign(
      updatedDesignData,
      designData
    );
    containerProps.updateContextState(designWithUpdatedDuration);
    containerProps.onSave(designWithUpdatedDuration, {});
  },

  /**
   * Take the current selection and select grouped elements instead of groups where applicable
   * @param {object} containerProps - Props of the calling container.
   */
  onFlattenSelection(containerProps) {
    Logger.info("ActionBarOps.onFlattenSelection called");
    const newSelection = EditorElementOps.flattenSelection(
      containerProps.selectedItems,
      containerProps.designData
    );
    containerProps.updateContextState({ selectedItems: newSelection });
  },

  /**
   * Update the supplied attributes of the selected items.
   *
   * @param attributes - The attributes of the text that are to be changed.
   * @param selectedItems - The selected text items
   * @param containerProps - The container props containing additional data required to process
   * the action.
   * @param callback - Callback function to execute on completion.
   * @returns {object} updated design data object.
   */
  onSelectedElementsAttributesChange(
    attributes,
    selectedItems,
    containerProps,
    callback
  ) {
    Logger.info("ActionbarOps.onSelectedElementsAttributesChange called");

    const updatedSelectedPreviews = EditorElementOps.updatePreviews(
      selectedItems,
      containerProps.elementPreviews,
      attributes
    );

    // persist the current elementPreviews as well as updated
    const updatedPreviews = {
      ...containerProps.elementPreviews,
      ...updatedSelectedPreviews
    };

    const { designData } = containerProps;
    const updatedDesignData = designData.updateElementsAttribute(
      {
        elementsId: selectedItems.map(item => item.itemId),
        attributes
      },
      callback
    );

    const updatedContextState = {
      designData: updatedDesignData,
      lastAttributeChanges: selectedItems.map(({ itemId, pageId }) => ({
        attributes,
        uniqueId: itemId,
        pageId,
        originalElement: designData.elements[itemId]
      })),
      elementPreviews: updatedPreviews,
      selectedItems: containerProps.selectedItems.map(item => ({
        ...item,
        preview: {}
      }))
    };

    containerProps.onSave(updatedDesignData, updatedContextState);
    containerProps.updateContextState(updatedContextState);

    // Some callers require this to add to update collection queue
    return updatedDesignData;
  }
};

export default ActionBarOps;
